import React, { useState, useEffect, useContext, useRef } from "react";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import CollapseHeader from "../../../../core/common/collapse-header";
import { all_routes } from "../../../router/all_routes";
import ComposeEmail from "./ComposeEmail";
import EmailService, {
  EmailMessage,
  PaginatedEmailResponse,
  BulkMailOperationPayload,
} from "./email.service";
import {
  formatEmailDate,
  extractSenderInfo,
  getEmailProviderTag,
  hasAttachments,
  getCrmEntityTag,
  getCrmEntityTooltip
} from "./emailUtils";
import { AuthContext } from "../../../../context/authcontext";
import { useEmailContext } from "../../../../context/emailContext";
import { showErrorAlert, showSuccessAlert, showDeleteConfirmationAlert } from "../../../../utils/alertUtils";
import { useEmailProviders } from "../../../../hooks/useEmailProviders";
import NoAccountConnected from "../../../../components/NoAccountConnected";
import { Dropdown } from "bootstrap";
import { useTenantChange } from "../../../../hooks/useTenantChange";
import { getLifecycleTypeName } from "../../../../utils/lifecycleUtils";

import EmailTabs from "./emailTabs";

interface EmailListProps {
  type: 'inbox' | 'important' | 'sent' | 'draft' | 'spam' | 'trash' | 'folder';
}

const EmailList: React.FC<EmailListProps> = ({ type }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { folderId: folderIdParam } = useParams<{ folderId?: string }>();
  const isFolderView = type === 'folder';
  const folderId =
    isFolderView && folderIdParam && !Number.isNaN(Number(folderIdParam))
      ? Number(folderIdParam)
      : undefined;
  const { setSelectedEmail, getMailFolderById } = useEmailContext();
  const folderInfo = isFolderView && folderId !== undefined ? getMailFolderById(folderId) : undefined;
  const {
    auth,
    unreadCount,
    setUnreadCount,
    importantUnreadCount,
    setImportantUnreadCount,
    updateEmailCounts,
  } = useContext<any>(AuthContext);
  const { hasConnectedProviders, loading: providersLoading, providers } = useEmailProviders();
  const [activeTab, setActiveTab] = useState(type);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [minimizedEmails, setMinimizedEmails] = useState<number[]>([]);
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [starredEmails, setStarredEmails] = useState<Set<string>>(new Set());
  const [emailResponse, setEmailResponse] = useState<PaginatedEmailResponse>({
    emails: [],
    totalCount: 0,
    currentPage: 1,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const searchParams = new URLSearchParams(location.search);
  const initialPage = parseInt(searchParams.get("page") || "1", 10);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredEmails, setFilteredEmails] = useState<EmailMessage[]>([]);
  const currentTabRef = useRef(activeTab);
  currentTabRef.current = activeTab;
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;
  const isFolderViewRef = useRef(isFolderView);
  isFolderViewRef.current = isFolderView;
  const folderIdRef = useRef(folderId);
  folderIdRef.current = folderId;
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const actionDropdownRef = useRef<HTMLButtonElement>(null);

  const isEmailForTab = (email: any, tab: string) => {
    if (!email) return false;
    if (tab === 'trash') return Boolean(email.is_trash);
    if (email.is_trash) return false;
    if (tab === 'important') return Boolean(email.is_important);
    const typeKey = email.mail_message_type_id?.mail_message_type_key?.toUpperCase();
    if (tab === 'draft') return typeKey === 'DRAFT';
    if (tab === 'sent') return typeKey === 'SENT';
    if (tab === 'spam') return typeKey === 'SPAM';
    if (tab === 'inbox') return typeKey === 'INBOX' || !typeKey;
    return true;
  };

  useEffect(() => {
    setActiveTab(type);
    setCurrentPage(1);
    setSearchQuery("");
    setSelectedEmails(new Set());
    setFilteredEmails([]);
    setEmailResponse({
      emails: [],
      totalCount: 0,
      currentPage: 1,
      hasNextPage: false,
      hasPreviousPage: false,
    });
    setLoading(true);
  }, [type]);

  const clearEmailData = () => {
    EmailService.clearEmailCache();
    setEmailResponse({
      emails: [],
      totalCount: 0,
      currentPage: 1,
      hasNextPage: false,
      hasPreviousPage: false,
    });
    setFilteredEmails([]);
    setSelectedEmails(new Set());
    setStarredEmails(new Set());
    setCurrentPage(1);
    setSearchQuery("");
    setError(null);
    setLoading(true);
  };

  const refreshEmails = async () => {
    try {
      setRefreshing(true);
      const userId = auth?.user_id?.toString() || "";
      if (!userId) return;

      const providers = await EmailService.getMailProviders(userId);
      if (!providers || providers.length === 0) {
        setRefreshing(false);
        setLoading(false);
        return;
      }

      const targetTab = currentTabRef.current;
      const targetPage = currentPageRef.current;
      const targetIsFolder = isFolderViewRef.current;
      const targetFolderId = folderIdRef.current;

      const response = targetIsFolder && targetFolderId !== undefined
        ? await EmailService.getEmailsByFolder(targetFolderId, targetPage, 25, true)
        : await EmailService.getEmailsByType(userId, targetTab, targetPage, 25, true);

      if (targetTab !== currentTabRef.current || targetIsFolder !== isFolderViewRef.current || targetFolderId !== folderIdRef.current) {
        return;
      }

      const validEmails = targetIsFolder
        ? response.emails
        : response.emails.filter((email: any) => isEmailForTab(email, targetTab));
      const cleanResponse = { ...response, emails: validEmails };

      if (validEmails.length === 0 && targetPage > 1) {
        setCurrentPage(1);
        const firstPageResponse = targetIsFolder && targetFolderId !== undefined
          ? await EmailService.getEmailsByFolder(targetFolderId, 1, 25, true)
          : await EmailService.getEmailsByType(userId, targetTab, 1, 25, true);
        if (targetTab !== currentTabRef.current || targetIsFolder !== isFolderViewRef.current || targetFolderId !== folderIdRef.current) {
          return;
        }
        const firstValidEmails = targetIsFolder
          ? firstPageResponse.emails
          : firstPageResponse.emails.filter((email: any) => isEmailForTab(email, targetTab));
        const cleanFirstResponse = { ...firstPageResponse, emails: firstValidEmails };
        setEmailResponse(cleanFirstResponse);
        setFilteredEmails(firstValidEmails);
      } else {
        setEmailResponse(cleanResponse);
        setFilteredEmails(validEmails);
      }

      const importantEmails = validEmails.filter(
        (email: any) => email.is_important
      );
      setStarredEmails(
        new Set(
          importantEmails.map((email: any) => email.mail_message_id.toString())
        )
      );
    } catch (err) {
      console.error("Error refreshing emails:", err);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useTenantChange({
    onTenantChange: () => {
      clearEmailData();
    },
    refreshData: refreshEmails,
    clearData: clearEmailData,
  });

  const handleEmailClick = (emailId: string) => {
    const email = emailResponse.emails.find(
      (e) => e.mail_message_id.toString() === emailId
    );
    if (email) {
      setSelectedEmail(email);
    }

    const sectionSlug = isFolderView && folderId !== undefined ? `folder-${folderId}` : activeTab;
    const detailRoute = all_routes.emailDetail
      .replace(":section", sectionSlug)
      .replace(":id", emailId);

    const routeWithPage =
      currentPage > 1 ? `${detailRoute}?page=${currentPage}` : detailRoute;
    navigate(routeWithPage);
  };

  const handleCheckboxClick = (e: React.MouseEvent, emailId: string) => {
    e.stopPropagation();
    setSelectedEmails((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(emailId)) {
        newSet.delete(emailId);
      } else {
        newSet.add(emailId);
      }
      return newSet;
    });
  };

  const handleStarClick = (e: React.MouseEvent, emailId: string) => {
    e.stopPropagation();
    setStarredEmails((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(emailId)) {
        newSet.delete(emailId);
      } else {
        newSet.add(emailId);
      }
      return newSet;
    });
  };

  const handleStarClickWithAPI = async (
    e: React.MouseEvent,
    emailId: string
  ) => {
    e.stopPropagation();

    try {
      const emailIdNum = parseInt(emailId);
      const isImportant = starredEmails.has(emailId);
      const newImportanceStatus = await EmailService.toggleEmailImportance(
        emailIdNum,
        isImportant
      );

      setStarredEmails((prev) => {
        const newSet = new Set(prev);
        if (newImportanceStatus) {
          newSet.add(emailId);
        } else {
          newSet.delete(emailId);
        }
        return newSet;
      });

      setEmailResponse((prev) => ({
        ...prev,
        emails: prev.emails.map((email: any) =>
          email.mail_message_id.toString() === emailId
            ? { ...email, is_important: newImportanceStatus }
            : email
        ),
      }));

      if (newImportanceStatus) {
        setImportantUnreadCount(importantUnreadCount + 1);
      } else {
        setImportantUnreadCount(Math.max(0, importantUnreadCount - 1));
      }
    } catch (err) {
      console.error("Error toggling star:", err);
      showErrorAlert("Failed to update star status");
    }
  };

  const handleSelectAll = () => {
    const allEmailIds = emailResponse.emails.map((email: any) =>
      email.mail_message_id.toString()
    );
    setSelectedEmails(new Set(allEmailIds));
  };

  const handleSelectNone = () => {
    setSelectedEmails(new Set());
  };

  const handleSelectRead = () => {
    const readEmailIds = emailResponse.emails
      .filter((email: any) => email.is_read)
      .map((email: any) => email.mail_message_id.toString());
    setSelectedEmails(new Set(readEmailIds));
  };

  const handleSelectUnread = () => {
    const unreadEmailIds = emailResponse.emails
      .filter((email: any) => !email.is_read)
      .map((email: any) => email.mail_message_id.toString());
    setSelectedEmails(new Set(unreadEmailIds));
  };

  const handleBulkOperation = async (
    operation: BulkMailOperationPayload["operation"]
  ) => {
    if (selectedEmails.size === 0) {
      showErrorAlert("Please select at least one email");
      return;
    }

    const effectiveOperation =
      activeTab === "trash" && operation === "trash" ? "delete" : operation;

    if (effectiveOperation === "delete") {
      const result = await showDeleteConfirmationAlert(
        selectedEmails.size > 1 ? "Delete Emails Permanently?" : "Delete Email Permanently?",
        "Are you sure you want to permanently delete the selected email(s)? This action cannot be undone.",
        "Yes, delete permanently",
        "Cancel"
      );
      if (!result.isConfirmed) {
        return;
      }
    }

    try {
      const mailMessageIds = Array.from(selectedEmails).map((id) =>
        parseInt(id)
      );

      await EmailService.bulkMailOperation({
        mail_message_ids: mailMessageIds,
        operation: effectiveOperation,
      });

      setEmailResponse((prev) => ({
        ...prev,
        emails: prev.emails
          .map((email: any) => {
            if (selectedEmails.has(email.mail_message_id.toString())) {
              const updatedEmail = { ...email };

              switch (effectiveOperation) {
                case "mark_read":
                  updatedEmail.is_read = true;
                  break;
                case "mark_unread":
                  updatedEmail.is_read = false;
                  break;

                case "mark_important":
                  updatedEmail.is_important = true;
                  break;
                case "unmark_important":
                  updatedEmail.is_important = false;
                  break;
                case "trash":
                  updatedEmail.is_trash = true;
                  return null;
                case "untrash":
                  updatedEmail.is_trash = false;
                  return null;
                case "delete":
                  return null;
              }

              return updatedEmail;
            }
            return email;
          })
          .filter(Boolean) as EmailMessage[],
      }));

      if (effectiveOperation === "mark_read") {
        setUnreadCount(Math.max(0, unreadCount - selectedEmails.size));
      } else if (effectiveOperation === "mark_unread") {
        setUnreadCount(unreadCount + selectedEmails.size);
      }

      const count = selectedEmails.size;
      setSelectedEmails(new Set());

      if (actionDropdownRef.current) {
        const dropdownElement = actionDropdownRef.current;
        const dropdownInstance = Dropdown.getInstance(dropdownElement);
        if (dropdownInstance) {
          dropdownInstance.hide();
        }
      }

      const operationMessages = {
        mark_read: count > 1 ? "Emails marked as read" : "Email marked as read",
        mark_unread: count > 1 ? "Emails marked as unread" : "Email marked as unread",

        mark_important: count > 1 ? "Emails marked as important" : "Email marked as important",
        unmark_important: count > 1 ? "Emails unmarked as important" : "Email unmarked as important",
        trash: count > 1 ? "Emails moved to trash" : "Email moved to trash",
        untrash: count > 1 ? "Emails restored from trash" : "Email restored from trash",
        delete: count > 1 ? "Emails deleted permanently" : "Email deleted Permanently",
      };

      showSuccessAlert(operationMessages[effectiveOperation]);

      await refreshEmails();
    } catch (error) {
      console.error("Error performing bulk operation:", error);
      showErrorAlert("Failed to perform bulk operation");
    }
  };

  const handleComposeClick = () => {
    setIsComposeOpen(true);
  };

  const handleClose = () => {
    setIsComposeOpen(false);
  };

  const handleMinimize = () => {
    setIsComposeOpen(false);
    setMinimizedEmails((prev) => [...prev, Date.now()]);
  };

  const handlePageChange = async (newPage: number) => {
    try {
      setPageLoading(true);
      setCurrentPage(newPage);
      const userId = auth?.user_id?.toString() || "";
      if (userId && hasConnectedProviders) {
        const response = isFolderView && folderId !== undefined
          ? await EmailService.getEmailsByFolder(folderId, newPage, 25)
          : await EmailService.getEmailsByType(userId, activeTab, newPage, 25);
        setEmailResponse(response);
      }
    } catch (err) {
      console.error("Error changing page:", err);
      showErrorAlert("Failed to load page");
    } finally {
      setPageLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      if (query.trim() === "") {
        const userId = auth?.user_id?.toString() || "";
        if (userId && hasConnectedProviders) {
          const useCache = sessionStorage.getItem("use_email_cache") === "true";
          if (useCache) {
            sessionStorage.removeItem("use_email_cache");
          }
          const forceRefresh = !useCache;

          const response = isFolderView && folderId !== undefined
            ? await EmailService.getEmailsByFolder(folderId, currentPage, 25, forceRefresh)
            : await EmailService.getEmailsByType(userId, activeTab, currentPage, 25, forceRefresh);
          const validEmails = isFolderView
            ? response.emails
            : response.emails.filter((email: any) => isEmailForTab(email, activeTab));
          setEmailResponse({ ...response, emails: validEmails });
          setFilteredEmails(validEmails);
        }
      } else {
        const userId = auth?.user_id?.toString() || "";
        if (userId && hasConnectedProviders) {
          try {
            const response = isFolderView && folderId !== undefined
              ? await EmailService.searchEmailsByFolder(folderId, query, currentPage, 25)
              : await EmailService.searchEmailsByType(userId, activeTab, query, currentPage, 25);
            const validEmails = isFolderView
              ? response.emails
              : response.emails.filter((email: any) => isEmailForTab(email, activeTab));
            setEmailResponse({ ...response, emails: validEmails });
            setFilteredEmails(validEmails);
          } catch (error) {
            console.error("Error searching emails:", error);
          }
        }
      }
    }, 500);
  };

  const clearSearch = async () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setSearchQuery("");
    const userId = auth?.user_id?.toString() || "";
    if (userId && hasConnectedProviders) {
      const useCache = sessionStorage.getItem("use_email_cache") === "true";
      if (useCache) {
        sessionStorage.removeItem("use_email_cache");
      }
      const forceRefresh = !useCache;

      const response = isFolderView && folderId !== undefined
        ? await EmailService.getEmailsByFolder(folderId, currentPage, 25, forceRefresh)
        : await EmailService.getEmailsByType(userId, activeTab, currentPage, 25, forceRefresh);
      const validEmails = isFolderView
        ? response.emails
        : response.emails.filter((email: any) => isEmailForTab(email, activeTab));
      setEmailResponse({ ...response, emails: validEmails });
      setFilteredEmails(validEmails);
    }
  };

  const truncateText = (text: string, maxLength: number = 50) => {
    if (!text) return "";
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  useEffect(() => {
    let cancelled = false;

    const loadEmails = () => {
      const useCache = sessionStorage.getItem("use_email_cache") === "true";
      if (useCache) {
        sessionStorage.removeItem("use_email_cache");
      }
      const forceRefresh = !useCache;

      const fetchEmails = async () => {
        try {
          if (!useCache) setLoading(true);
          setError(null);
          const userId = auth?.user_id?.toString() || "";
          if (!userId) {
            throw new Error("User ID not found");
          }

          if (!hasConnectedProviders) {
             setLoading(false);
             return;
          }

          const targetTab = activeTab;
          const targetFolderId = folderId;
          const isFolder = isFolderView;

          const response = isFolder && targetFolderId !== undefined
            ? await EmailService.getEmailsByFolder(targetFolderId, currentPage, 25, forceRefresh)
            : await EmailService.getEmailsByType(userId, targetTab, currentPage, 25, forceRefresh);
          if (cancelled) return;
          if (targetTab !== currentTabRef.current || isFolder !== isFolderViewRef.current || targetFolderId !== folderIdRef.current) return;

          const validEmails = isFolder
            ? response.emails
            : response.emails.filter((email: any) => isEmailForTab(email, targetTab));
          const cleanResponse = { ...response, emails: validEmails };

          setEmailResponse(cleanResponse);
          setFilteredEmails(validEmails);

          const importantEmails = validEmails.filter(
            (email: any) => email.is_important
          );
          setStarredEmails(
            new Set(
              importantEmails.map((email: any) => email.mail_message_id.toString())
            )
          );
        } catch (err) {
          if (cancelled) return;
          console.error("Error fetching emails:", err);
          setError(
            err instanceof Error ? err.message : "Failed to fetch emails"
          );
          showErrorAlert("Failed to fetch emails");
        } finally {
          if (!cancelled) setLoading(false);
        }
      };

      fetchEmails();
    };

    loadEmails();

    return () => {
      cancelled = true;
    };
  }, [auth?.user_id, activeTab, currentPage, hasConnectedProviders, isFolderView, folderId]);

  useEffect(() => {
    if (isFolderView) {
      setCurrentPage(1);
    }
  }, [folderId]);

  const loadCountsInBackground = async (userId: string) => {
    try {
      if (!providers || providers.length === 0) return;
      const stats = await EmailService.getMailStats();
      updateEmailCounts(stats.inbox || 0, stats.important || 0);
    } catch (err) {
      console.error("Error loading counts:", err);
    }
  };

  useEffect(() => {
    const loadCountsOnFirstVisit = async () => {
      const userId = auth?.user_id?.toString() || "";
      if (userId && !providersLoading) {
        if (providers && providers.length > 0) {
          const isFirstVisit = !sessionStorage.getItem('mailModuleVisited');
          if (isFirstVisit) {
            sessionStorage.setItem('mailModuleVisited', 'true');
            await loadCountsInBackground(userId);
          }
        } else {
          updateEmailCounts(0, 0);
        }
      }
    };

    loadCountsOnFirstVisit();
  }, [auth?.user_id, providers, providersLoading]);

  useEffect(() => {
    if (auth?.user_id) {
      refreshIntervalRef.current = setInterval(refreshEmails, 300000);

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }
  }, [auth?.user_id, currentPage, activeTab, isFolderView, folderId]);

  useEffect(() => {
    const handleRefreshSignal = () => {
      const refreshNeeded = localStorage.getItem("email_refresh_needed");
      const refreshTimestamp = localStorage.getItem("email_refresh_timestamp");

      if (refreshTimestamp) {
        const timestamp = parseInt(refreshTimestamp, 10);
        const now = Date.now();

        if (now - timestamp < 10000) {
          const currentTab = currentTabRef.current;
          if (!refreshNeeded || refreshNeeded === currentTab || refreshNeeded === "all") {
            localStorage.removeItem("email_refresh_needed");
            localStorage.removeItem("email_refresh_timestamp");
            refreshEmails();
          }
        }
      }
    };

    handleRefreshSignal();

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleRefreshSignal();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleRefreshSignal);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleRefreshSignal);
    };
  }, [auth?.user_id]);

  const emails =
    searchQuery.trim() === "" ? emailResponse.emails : filteredEmails;
  const selectedEmailItems = emails.filter(
    (email: any) => email?.mail_message_id && selectedEmails.has(email.mail_message_id.toString())
  );
  const canMarkRead = selectedEmailItems.length === 0 || selectedEmailItems.some((email: any) => !email.is_read);
  const canMarkUnread = selectedEmailItems.length === 0 || selectedEmailItems.some((email: any) => email.is_read);
  const canMarkImportant = selectedEmailItems.length === 0 || selectedEmailItems.some((email: any) => !email.is_important);
  const canUnmarkImportant = selectedEmailItems.length === 0 || selectedEmailItems.some((email: any) => email.is_important);
  const totalCount = emailResponse.totalCount;
  const hasNextPage = emailResponse.hasNextPage;
  const hasPreviousPage = emailResponse.hasPreviousPage;

  const pageSize = 25;
  let paginationText = "";

  if (totalCount <= pageSize) {
    paginationText = `${totalCount} out of ${totalCount}`;
  } else {
    const currentPageStart = (currentPage - 1) * pageSize + 1;
    const currentPageEnd = Math.min(currentPage * pageSize, totalCount);
    paginationText = `${currentPageEnd} out of ${totalCount}`;
  }

  const handleTabClick = (tab: 'inbox' | 'important' | 'sent' | 'draft' | 'spam' | 'trash') => {
    let url = all_routes.email;
    switch (tab) {
      case 'important': url = all_routes.emailImportant; break;
      case 'sent': url = all_routes.emailSent; break;
      case 'draft': url = all_routes.emailDraft; break;
      case 'spam': url = all_routes.emailSpam; break;
      case 'trash': url = all_routes.emailTrash; break;
    }

    if (tab === activeTab && !isFolderView) {
      return;
    }

    setActiveTab(tab);
    setCurrentPage(1);
    setSearchQuery("");
    setSelectedEmails(new Set());
    setFilteredEmails([]);
    setEmailResponse({
      emails: [],
      totalCount: 0,
      currentPage: 1,
      hasNextPage: false,
      hasPreviousPage: false,
    });
    setLoading(true);

    navigate(url);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="row">
            <div className="col-8">
              <h3 className="page-title">
                {isFolderView
                  ? folderInfo?.folder_display_name || "Folder"
                  : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
              </h3>
            </div>
            <div className="col-4 text-end">
              <div className="head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <EmailTabs
            activeTab={isFolderView ? null : (activeTab as Exclude<typeof activeTab, 'folder'>)}
            activeFolderId={folderId}
            onComposeClick={handleComposeClick}
            onTabClick={handleTabClick}
          />
          <div className="col-lg-9 col-md-12">
            <div className="card">
              <div className="card-body">
                {providersLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-2">
                      <small className="text-muted">Loading E-mails</small>
                    </div>
                  </div>
                ) : !hasConnectedProviders ? (
                  <NoAccountConnected />
                ) : (
                  <>
                    <div className="email-header">
                      <div className="row">
                        <div className="col-lg-9 top-action-left col-sm-12">
                          <div className="float-left">
                            <div className="btn-group dropdown-action me-1">
                              <button
                                type="button"
                                className={`btn btn-white dropdown-toggle ${!hasConnectedProviders ? 'disabled' : ''}`}
                                data-bs-toggle={hasConnectedProviders ? "dropdown" : undefined}
                                disabled={!hasConnectedProviders}
                                style={{ pointerEvents: !hasConnectedProviders ? 'none' : 'auto', opacity: !hasConnectedProviders ? '0.6' : '1' }}
                              >
                                Select <i className="fas fa-angle-down" />
                              </button>
                              <div className="dropdown-menu">
                                <Link
                                  className="dropdown-item"
                                  to="#"
                                  onClick={hasConnectedProviders ? handleSelectAll : undefined}
                                  style={{ pointerEvents: !hasConnectedProviders ? 'none' : 'auto' }}
                                >
                                  All
                                </Link>
                                <Link
                                  className="dropdown-item"
                                  to="#"
                                  onClick={hasConnectedProviders ? handleSelectNone : undefined}
                                  style={{ pointerEvents: !hasConnectedProviders ? 'none' : 'auto' }}
                                >
                                  None
                                </Link>
                                <div className="dropdown-divider" />
                                <Link
                                  className="dropdown-item"
                                  to="#"
                                  onClick={hasConnectedProviders ? handleSelectRead : undefined}
                                  style={{ pointerEvents: !hasConnectedProviders ? 'none' : 'auto' }}
                                >
                                  Read
                                </Link>
                                <Link
                                  className="dropdown-item"
                                  to="#"
                                  onClick={hasConnectedProviders ? handleSelectUnread : undefined}
                                  style={{ pointerEvents: !hasConnectedProviders ? 'none' : 'auto' }}
                                >
                                  Unread
                                </Link>
                              </div>
                            </div>
                            {selectedEmails.size > 0 && (
                              <div className="btn-group dropdown-action me-1">
                                <button
                                  ref={actionDropdownRef}
                                  type="button"
                                  className={`btn btn-white dropdown-toggle ${!hasConnectedProviders ? 'disabled' : ''}`}
                                  data-bs-toggle={hasConnectedProviders ? "dropdown" : undefined}
                                  disabled={selectedEmails.size === 0 || !hasConnectedProviders}
                                  style={{ pointerEvents: !hasConnectedProviders ? 'none' : 'auto', opacity: !hasConnectedProviders ? '0.6' : '1' }}
                                >
                                  Actions <i className="fas fa-angle-down" />
                                </button>
                                <div className="dropdown-menu" style={{ minWidth: "200px", whiteSpace: "nowrap" }}>
                                  {canMarkRead && (
                                    <button
                                      className="dropdown-item"
                                      onClick={hasConnectedProviders ? () =>
                                        handleBulkOperation("mark_read")
                                        : undefined}
                                      style={{ pointerEvents: !hasConnectedProviders ? 'none' : 'auto' }}
                                    >
                                      <i className="fas fa-check me-2"></i>
                                      Mark As Read
                                    </button>
                                  )}
                                  {canMarkUnread && (
                                    <button
                                      className="dropdown-item"
                                      onClick={hasConnectedProviders ? () =>
                                        handleBulkOperation("mark_unread")
                                        : undefined}
                                      style={{ pointerEvents: !hasConnectedProviders ? 'none' : 'auto' }}
                                    >
                                      <i className="fas fa-envelope me-2"></i>
                                      Mark As Unread
                                    </button>
                                  )}
                                  <div className="dropdown-divider" />
                                  {activeTab === "trash" ? (
                                    <button
                                      className="dropdown-item"
                                      onClick={hasConnectedProviders ? () =>
                                        handleBulkOperation("untrash")
                                        : undefined}
                                      style={{ pointerEvents: !hasConnectedProviders ? 'none' : 'auto' }}
                                    >
                                      <i className="fas fa-undo me-2"></i>
                                      Restore
                                    </button>
                                  ) : (
                                    <>
                                      {canMarkImportant && (
                                        <button
                                          className="dropdown-item"
                                          onClick={hasConnectedProviders ? () =>
                                            handleBulkOperation("mark_important")
                                            : undefined}
                                          style={{ pointerEvents: !hasConnectedProviders ? 'none' : 'auto' }}
                                        >
                                          <i className="fas fa-star me-2"></i>
                                          Mark As Important
                                        </button>
                                      )}
                                      {canUnmarkImportant && (
                                        <button
                                          className="dropdown-item"
                                          onClick={hasConnectedProviders ? () =>
                                            handleBulkOperation("unmark_important")
                                            : undefined}
                                          style={{ pointerEvents: !hasConnectedProviders ? 'none' : 'auto' }}
                                        >
                                          <i className="far fa-star me-2"></i>
                                          Unmark As Important
                                        </button>
                                      )}
                                    </>
                                  )}
                                  <div className="dropdown-divider" />
                                  <button
                                    className="dropdown-item text-danger"
                                    onClick={hasConnectedProviders ? () => handleBulkOperation(activeTab === "trash" ? "delete" : "trash") : undefined}
                                    style={{ pointerEvents: !hasConnectedProviders ? 'none' : 'auto' }}
                                  >
                                    <i className="fas fa-trash me-2"></i>
                                    {activeTab === "trash" ? "Delete Permanently" : "Delete"}
                                  </button>
                                </div>
                              </div>
                            )}

                            <div className="btn-group dropdown-action mail-search">
                              <div className="input-group">
                                <input
                                  type="text"
                                  placeholder="Search Messages"
                                  className={`form-control search-message ${!hasConnectedProviders ? 'disabled' : ''}`}
                                  value={searchQuery ?? ""}
                                  onChange={hasConnectedProviders ? handleSearchChange : undefined}
                                  disabled={!hasConnectedProviders}
                                  style={{ opacity: !hasConnectedProviders ? '0.6' : '1' }}
                                />
                                {searchQuery && hasConnectedProviders && (
                                  <button
                                    className="btn btn-outline-primary"
                                    type="button"
                                    onClick={clearSearch}
                                    title="Clear search"
                                  >
                                    <i className="fas fa-times"></i>
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-3 top-action-right col-sm-12">
                          <div className="text-end ">
                            <button
                              type="button"
                              title="Refresh"
                              data-toggle="tooltip"
                              className={`btn btn-white d-none d-md-inline-block me-1 ${!hasConnectedProviders ? 'disabled' : ''}`}
                              onClick={hasConnectedProviders ? refreshEmails : undefined}
                              disabled={refreshing || !hasConnectedProviders}
                              style={{ pointerEvents: !hasConnectedProviders ? 'none' : 'auto', opacity: !hasConnectedProviders ? '0.6' : '1' }}
                            >
                              <i
                                className={`fas fa-sync-alt ${refreshing ? "fa-spin" : ""
                                  }`}
                              />
                            </button>
                            <div className="btn-group">
                              <button
                                className={`btn btn-white ${!hasConnectedProviders ? 'disabled' : ''}`}
                                disabled={!hasPreviousPage || pageLoading || !hasConnectedProviders}
                                onClick={hasConnectedProviders ? () =>
                                  handlePageChange(currentPage - 1)
                                  : undefined}
                                style={{ pointerEvents: !hasConnectedProviders ? 'none' : 'auto', opacity: !hasConnectedProviders ? '0.6' : '1' }}
                              >
                                <i className="fas fa-angle-left" />
                              </button>
                              <button
                                className={`btn btn-white ${!hasConnectedProviders ? 'disabled' : ''}`}
                                disabled={!hasNextPage || pageLoading || !hasConnectedProviders}
                                onClick={hasConnectedProviders ? () =>
                                  handlePageChange(currentPage + 1)
                                  : undefined}
                                style={{ pointerEvents: !hasConnectedProviders ? 'none' : 'auto', opacity: !hasConnectedProviders ? '0.6' : '1' }}
                              >
                                {pageLoading ? (
                                  <i className="fas fa-spinner fa-spin" />
                                ) : (
                                  <i className="fas fa-angle-right" />
                                )}
                              </button>
                            </div>
                          </div>
                          <div className="text-end">
                            <span className="text-muted d-none d-md-inline-block">
                              {paginationText}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="email-content pt-3">
                      <div className="table-responsive">
                        {loading || pageLoading ? (
                          <div className="text-center py-4">
                            <div
                              className="spinner-border text-primary"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading E-mails
                              </span>
                            </div>
                            <div className="mt-2">
                              <small className="text-muted">
                                Loading E-mails
                              </small>
                            </div>
                          </div>
                        ) : error ? (
                          <div className="text-center py-4">
                            <div className="alert alert-danger">{error}</div>
                          </div>
                        ) : emails.length === 0 ? (
                          <div className="text-center py-4 mt-5 mb-5">
                            <i className="fa-solid fa-envelope-circle-check text-primary" style={{ fontSize: "50px" }}></i>
                            <p className="text-muted" style={{ fontSize: "25px" }}>No emails found</p>
                          </div>
                        ) : (
                          <table className="table table-inbox table-hover">
                            <tbody>
                              {emails.map((email: any) => {
                                if (!email || !email.mail_message_id) {
                                  return null;
                                }
                                const senderInfo = extractSenderInfo(
                                  email.send_from || ""
                                );
                                const emailId =
                                  email.mail_message_id.toString();
                                const isUnread = !email.is_read;
                                const providerTag = getEmailProviderTag(
                                  email.mail_provider_key || ""
                                );

                                return (
                                  <tr
                                    key={emailId}
                                    className={`${isUnread ? "unread" : ""
                                      } clickable-row`}
                                    onClick={() => handleEmailClick(emailId)}
                                    style={{ cursor: "pointer" }}
                                  >
                                    <td
                                      style={{ width: "40px" }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <div className="form-check form-check-md">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          checked={!!selectedEmails.has(emailId)}
                                          onChange={(e) =>
                                            handleCheckboxClick(
                                              e as any,
                                              emailId
                                            )
                                          }
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      </div>
                                    </td>
                                    <td
                                      style={{ width: "40px" }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <span
                                        className="mail-important"
                                        onClick={(e) =>
                                          handleStarClickWithAPI(e, emailId)
                                        }
                                        style={{ cursor: "pointer" }}
                                      >
                                        <i
                                          className={`fas fa-star ${email.is_important ? "starred" : ""
                                            }`}
                                        />
                                      </span>
                                    </td>
                                    <td
                                      className="name"
                                      style={{
                                        width: "180px",
                                        maxWidth: "180px",
                                      }}
                                    >
                                      <div
                                        className="text-truncate"
                                        title={senderInfo.name}
                                      >
                                        {senderInfo.name}
                                      </div>
                                    </td>
                                    <td
                                      className="subject"
                                      style={{ width: "100%", maxWidth: 0 }}
                                    >
                                      <div
                                        className="text-truncate"
                                        title={email.subject}
                                      >
                                        {email.subject}
                                      </div>
                                    </td>
                                    <td style={{ width: "40px" }}>
                                      {hasAttachments(email) && (
                                        <i
                                          className="fas fa-paperclip text-muted"
                                          title="Has attachments"
                                          style={{ fontSize: "14px" }}
                                        />
                                      )}
                                    </td>
                                    <td
                                      className="mail-date"
                                      style={{ width: "auto", whiteSpace: "nowrap", paddingRight: "15px" }}
                                    >
                                      <div className="d-flex align-items-center justify-content-end">
                                        <div className="d-flex align-items-center">
                                          {email.crm_entity?.crm_entity_key && (() => {
                                            const crmTag = getCrmEntityTag(email.crm_entity.crm_entity_key);
                                            return (
                                              <span
                                                className="badge me-2"
                                                title={getCrmEntityTooltip(['LEAD', 'PIPELINE', 'DEAL'].includes(email.crm_entity.crm_entity_key) ? getLifecycleTypeName(email.crm_entity.crm_entity_key) : email.crm_entity.crm_entity_display_name, activeTab)}
                                                style={{
                                                  backgroundColor: crmTag.bgColor,
                                                  color: crmTag.color,
                                                  fontSize: "0.7rem",
                                                  padding: "0.2rem 0.4rem",
                                                  borderRadius: "4px",
                                                  fontWeight: "500",
                                                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                                  cursor: "help"
                                                }}
                                              >
                                                <i className={`${crmTag.icon} me-1`} style={{ fontSize: "0.8rem" }}></i>
                                                {['LEAD', 'PIPELINE', 'DEAL'].includes(email.crm_entity.crm_entity_key) ? getLifecycleTypeName(email.crm_entity.crm_entity_key) : email.crm_entity.crm_entity_display_name}
                                              </span>
                                            );
                                          })()}
                                          <span
                                            className="badge"
                                            style={{
                                              backgroundColor:
                                                providerTag.bgColor,
                                              color: providerTag.color,
                                              fontSize: "0.7rem",
                                              padding: "0.2rem 0.4rem",
                                              borderRadius: "4px",
                                              fontWeight: "500",
                                              boxShadow:
                                                "0 1px 3px rgba(0,0,0,0.1)",
                                              marginRight: "12px",
                                            }}
                                          >
                                            <i
                                              className={providerTag.icon}
                                              style={{
                                                marginRight: "0.2rem",
                                                fontSize: "0.8rem",
                                              }}
                                            ></i>
                                            {providerTag.label}
                                          </span>
                                        </div>
                                        <span
                                          className="text-muted small"
                                          style={{ minWidth: "fit-content" }}
                                        >
                                          {formatEmailDate(
                                            email.email_datetime
                                          )}
                                        </span>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <ComposeEmail
          isOpen={isComposeOpen}
          onClose={handleClose}
          onMinimize={handleMinimize}
          cachedProviders={providers}
        />

        {minimizedEmails.length > 0 && (
          <div className="minimized-emails">
            {minimizedEmails.map((id) => (
              <div
                key={id}
                className="minimized-email"
                onClick={() => {
                  setIsComposeOpen(true);
                  setMinimizedEmails((prev) =>
                    prev.filter((emailId) => emailId !== id)
                  );
                }}
              >
                New Message
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailList;






